import React, { useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FileText,
  ArrowRight,
  Users,
  CheckCircle2,
  Sparkles,
  Share2,
  Mail,
  Check,
  Crown,
  Star,
  ShieldCheck,
  Smartphone,
  BarChart3,
  MessageSquare,
  Palette,
  PenLine,
} from "lucide-react";
import { ProductHeader } from "@core/components/layout/products/ProductHeader";
import { ProductSidebar } from "@core/components/layout/products/ProductSidebar";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@core/components/ui/accordion";
import { useAuth } from "@core/contexts";

const RETURNING_VISITOR_KEY = "dooquest_has_visited";

export default function LandingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading } = useAuth();
  const { t } = useTranslation("forms");
  const forceHome = searchParams.get("home") === "1";

  useEffect(() => {
    if (loading || forceHome) return;

    if (user) {
      navigate("/dashboard", { replace: true });
      return;
    }

    const hasVisited = localStorage.getItem(RETURNING_VISITOR_KEY);
    if (hasVisited) {
      navigate("/create", { replace: true });
      return;
    }

    localStorage.setItem(RETURNING_VISITOR_KEY, "1");
  }, [user, loading, navigate, forceHome]);

  return (
    <div className="flex min-h-screen-safe bg-[#030712] text-white">
      {/* ProductSidebar */}
      <ProductSidebar productType="form" />

      {/* Contenu principal */}
      <div className="flex-1 min-w-0 w-full">
        {/* ProductHeader */}
        <ProductHeader productType="form" showAuth={true} />

        {/* Contenu de la page */}
        {/* IMPORTANT: NE PAS AJOUTER DE Z-INDEX ICI - Crée des contextes d'empilement qui bloquent le widget de support */}
        <div className="relative py-20">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid items-center gap-12 lg:grid-cols-2">
              {/* Left: Text + CTA */}
              <div className="text-center lg:text-left">
                {/* Headline */}
                <h1 className="text-3xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight mb-6 leading-[1.1]">
                  {t("landing.hero.title_line1")}{" "}
                  <span className="bg-gradient-to-r from-purple-400 via-indigo-400 to-purple-400 bg-clip-text text-transparent">
                    {t("landing.hero.title_highlight")}
                  </span>
                </h1>

                <p className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto lg:mx-0 mb-12 leading-relaxed">
                  {t("landing.hero.subtitle")}
                </p>

                {/* CTA */}
                <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4">
                  <Link
                    to="/create"
                    data-testid="create-form-poll-button"
                    className="group relative px-8 py-4 text-base font-medium bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-400 hover:to-indigo-400 rounded-xl shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 transition-all duration-300 text-white inline-flex"
                  >
                    <span className="flex items-center gap-2">
                      <FileText className="w-5 h-5" />
                      {t("landing.hero.cta_create")}
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </Link>
                </div>
              </div>

              {/* Right: Aperçu du formulaire */}
              <div className="relative mx-auto w-full max-w-2xl lg:max-w-none">
                <div className="relative rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-sm p-8 shadow-2xl overflow-hidden">
                  <span className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#030712]/40 via-transparent to-transparent" />
                  <div className="flex items-center gap-4 mb-6">
                    <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
                      <div className="h-full w-3/5 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full" />
                    </div>
                    <span className="text-sm text-gray-400">
                      {t("landing.preview.progress")}
                    </span>
                  </div>

                  <div className="text-center mb-8">
                    <h3 className="text-xl font-semibold mb-2">
                      {t("landing.preview.question_title")}
                    </h3>
                    <p className="text-sm text-gray-400">
                      {t("landing.preview.select_option")}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 gap-4 mb-6">
                    {[
                      t("landing.preview.option_very_satisfied"),
                      t("landing.preview.option_satisfied"),
                      t("landing.preview.option_neutral"),
                      t("landing.preview.option_dissatisfied"),
                    ].map((option, i) => (
                      <div
                        key={option}
                        className={`p-4 rounded-xl text-center font-medium transition-all ${
                          i === 0
                            ? "bg-purple-500/20 border-2 border-purple-500 text-purple-300"
                            : "bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10"
                        }`}
                      >
                        {option}
                      </div>
                    ))}
                  </div>

                  <div className="flex justify-between mt-8">
                    <button className="px-4 py-2 border border-white/10 rounded-lg text-sm text-gray-400 hover:bg-white/5">
                      {t("landing.preview.previous")}
                    </button>
                    <button className="px-4 py-2 bg-purple-500 rounded-lg text-sm text-white hover:bg-purple-600">
                      {t("landing.preview.next")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Processus 1-2-3 */}
          <section className="relative py-24 border-t border-white/5">
            <div className="max-w-6xl mx-auto px-6">
              <div className="text-center mb-16">
                <span className="inline-block px-3 py-1 bg-purple-500/10 border border-purple-500/20 rounded-full text-xs text-purple-300 mb-4">
                  {t("landing.process.badge")}
                </span>
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  {t("landing.process.title_pre")}{" "}
                  <span className="text-purple-400">
                    {t("landing.process.title_highlight")}
                  </span>
                </h2>
                <p className="text-gray-400 max-w-xl mx-auto">
                  {t("landing.process.subtitle")}
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-8 relative">
                {[
                  {
                    step: "1",
                    icon: PenLine,
                    title: t("landing.process.step1_title"),
                    desc: t("landing.process.step1_desc"),
                  },
                  {
                    step: "2",
                    icon: Share2,
                    title: t("landing.process.step2_title"),
                    desc: t("landing.process.step2_desc"),
                  },
                  {
                    step: "3",
                    icon: BarChart3,
                    title: t("landing.process.step3_title"),
                    desc: t("landing.process.step3_desc"),
                  },
                ].map((item) => (
                  <div
                    key={item.step}
                    className="relative p-8 rounded-2xl border border-white/5 bg-white/[0.02] hover:bg-white/[0.04] transition-all duration-300 text-center"
                  >
                    <div className="absolute -top-5 left-1/2 -translate-x-1/2 w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-purple-500/30 text-white font-bold">
                      {item.step}
                    </div>
                    <div className="mt-4 w-14 h-14 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center mx-auto mb-4">
                      <item.icon className="w-7 h-7 text-purple-400" />
                    </div>
                    <h3 className="text-lg font-semibold mb-3">{item.title}</h3>
                    <p className="text-sm text-gray-400 leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Features Section */}
          {/* IMPORTANT: NE PAS AJOUTER DE Z-INDEX - Section de contenu normal, pas besoin de contexte d'empilement */}
          <section className="relative py-24 border-t border-white/5">
            <div className="max-w-6xl mx-auto px-6">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  {t("landing.features.title_pre")}{" "}
                  <span className="text-purple-400">
                    {t("landing.features.title_highlight")}
                  </span>
                </h2>
                <p className="text-gray-400 max-w-xl mx-auto">
                  {t("landing.features.subtitle")}
                </p>
              </div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
                {[
                  {
                    icon: Palette,
                    title: t("landing.features.customization_title"),
                    desc: t("landing.features.customization_desc"),
                    examples: [
                      t("landing.features.customization_ex1"),
                      t("landing.features.customization_ex2"),
                      t("landing.features.customization_ex3"),
                    ],
                  },
                  {
                    icon: MessageSquare,
                    title: t("landing.features.conditional_title"),
                    desc: t("landing.features.conditional_desc"),
                    examples: [
                      t("landing.features.conditional_ex1"),
                      t("landing.features.conditional_ex2"),
                      t("landing.features.conditional_ex3"),
                    ],
                  },
                  {
                    icon: Sparkles,
                    title: t("landing.features.ai_title"),
                    desc: t("landing.features.ai_desc"),
                    examples: [
                      t("landing.features.ai_ex1"),
                      t("landing.features.ai_ex2"),
                      t("landing.features.ai_ex3"),
                    ],
                  },
                  {
                    icon: BarChart3,
                    title: t("landing.features.analytics_title"),
                    desc: t("landing.features.analytics_desc"),
                    examples: [
                      t("landing.features.analytics_ex1"),
                      t("landing.features.analytics_ex2"),
                      t("landing.features.analytics_ex3"),
                    ],
                  },
                  {
                    icon: ShieldCheck,
                    title: t("landing.features.noads_title"),
                    desc: t("landing.features.noads_desc"),
                    examples: [
                      t("landing.features.noads_ex1"),
                      t("landing.features.noads_ex2"),
                      t("landing.features.noads_ex3"),
                    ],
                  },
                ].map((feature) => (
                  <div
                    key={feature.title}
                    className="group p-6 rounded-2xl border border-white/5 bg-white/[0.01] hover:bg-white/[0.03] transition-all duration-500"
                  >
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                      <feature.icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-gray-400 leading-relaxed mb-4">
                      {feature.desc}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {feature.examples.map((example, i) => (
                        <span
                          key={i}
                          className="px-3 py-1 bg-purple-500/10 border border-purple-500/20 rounded-full text-xs text-purple-300"
                        >
                          {example}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Qui utilise DooQuest ? */}
          <section className="relative py-24 border-t border-white/5">
            <div className="max-w-6xl mx-auto px-6">
              <div className="text-center mb-16">
                <span className="inline-block px-3 py-1 bg-purple-500/10 border border-purple-500/20 rounded-full text-xs text-purple-300 mb-4">
                  {t("landing.personas.badge")}
                </span>
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  {t("landing.personas.title_pre")}{" "}
                  <span className="text-purple-400">DooQuest</span>{" "}
                  {t("landing.personas.title_suffix")}
                </h2>
              </div>
              <div className="grid md:grid-cols-3 gap-6">
                {[
                  {
                    emoji: "🏢",
                    title: t("landing.personas.business_title"),
                    desc: t("landing.personas.business_desc"),
                  },
                  {
                    emoji: "🎓",
                    title: t("landing.personas.teachers_title"),
                    desc: t("landing.personas.teachers_desc"),
                  },
                  {
                    emoji: "🎉",
                    title: t("landing.personas.orgs_title"),
                    desc: t("landing.personas.orgs_desc"),
                  },
                ].map((item) => (
                  <div
                    key={item.title}
                    className="p-6 rounded-2xl border border-white/5 bg-white/[0.02] hover:bg-white/[0.04] transition-all duration-300 text-center"
                  >
                    <div className="text-4xl mb-4">{item.emoji}</div>
                    <h3 className="text-lg font-semibold mb-3">{item.title}</h3>
                    <p className="text-sm text-gray-400 leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="relative py-24 border-t border-white/5">
            <div className="max-w-3xl mx-auto px-6">
              <div className="text-center mb-16">
                <span className="inline-block px-3 py-1 bg-purple-500/10 border border-purple-500/20 rounded-full text-xs text-purple-300 mb-4">
                  {t("landing.faq.badge")}
                </span>
                <h2 className="text-3xl md:text-4xl font-bold">
                  {t("landing.faq.title_pre")}{" "}
                  <span className="text-purple-400">
                    {t("landing.faq.title_highlight")}
                  </span>
                </h2>
              </div>
              <Accordion type="single" collapsible className="space-y-2">
                {[
                  {
                    value: "q1",
                    question: t("landing.faq.q1_question"),
                    answer: t("landing.faq.q1_answer"),
                    link: { label: t("landing.faq.q1_link"), href: "/docs" },
                  },
                  {
                    value: "q2",
                    question: t("landing.faq.q2_question"),
                    answer: t("landing.faq.q2_answer"),
                    link: { label: t("landing.faq.q2_link"), href: "/docs" },
                  },
                  {
                    value: "q3",
                    question: t("landing.faq.q3_question"),
                    answer: t("landing.faq.q3_answer"),
                    link: { label: t("landing.faq.q3_link"), href: "/docs" },
                  },
                  {
                    value: "q4",
                    question: t("landing.faq.q4_question"),
                    answer: t("landing.faq.q4_answer"),
                    link: {
                      label: t("landing.faq.q4_link"),
                      href: "/privacy",
                    },
                  },
                ].map((item) => (
                  <AccordionItem
                    key={item.value}
                    value={item.value}
                    className="border border-white/5 rounded-xl px-6 bg-white/[0.01]"
                  >
                    <AccordionTrigger className="text-left hover:no-underline text-white hover:text-purple-300">
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-400">
                      <p className="mb-3">{item.answer}</p>
                      <Link
                        to={item.link.href}
                        className="text-purple-400 hover:text-purple-300 text-sm transition-colors"
                      >
                        {item.link.label}
                      </Link>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </section>

          {/* Tableau comparatif */}
          <section className="relative py-24 border-t border-white/5">
            <div className="max-w-4xl mx-auto px-6">
              <div className="text-center mb-16">
                <span className="inline-block px-3 py-1 bg-purple-500/10 border border-purple-500/20 rounded-full text-xs text-purple-300 mb-4">
                  {t("landing.comparison.badge")}
                </span>
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  {t("landing.comparison.title_pre")}{" "}
                  <span className="text-purple-400">
                    {t("landing.comparison.title_highlight")}
                  </span>
                </h2>
              </div>
              <div className="overflow-x-auto rounded-2xl border border-white/5">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/5">
                      <th className="text-left p-4 text-gray-400 font-medium">
                        {t("landing.comparison.col_feature")}
                      </th>
                      <th className="p-4 text-center font-semibold text-purple-400 bg-purple-500/5">
                        DooQuest
                      </th>
                      <th className="p-4 text-center text-gray-400 font-medium">
                        Google Forms
                      </th>
                      <th className="hidden sm:table-cell p-4 text-center text-gray-400 font-medium">
                        SurveyMonkey
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      [t("landing.comparison.row_ai_create"), "✓", "—", "✓"],
                      [t("landing.comparison.row_ai_analyze"), "✓", "—", "✓"],
                      [t("landing.comparison.row_unlimited"), "✓", "✓", "—"],
                      [t("landing.comparison.row_export"), "✓", "✓", "—"],
                      [t("landing.comparison.row_conditional"), "✓", "✓", "✓"],
                      [t("landing.comparison.row_no_account"), "✓", "✓", "✓"],
                      [t("landing.comparison.row_language"), "✓", "✓", "✓"],
                    ].map(([feature, df, gf, tf], i) => (
                      <tr
                        key={i}
                        className="border-b border-white/5 last:border-0 hover:bg-white/[0.02] transition-colors"
                      >
                        <td className="p-4 text-gray-300">{feature}</td>
                        <td className="p-4 text-center font-medium text-purple-400 bg-purple-500/5">
                          {df}
                        </td>
                        <td className="p-4 text-center text-gray-400">{gf}</td>
                        <td className="hidden sm:table-cell p-4 text-center text-gray-400">
                          {tf}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          {/* Tarifs — preview (détails complets sur /pricing) */}
          <section className="relative py-24 border-t border-white/5">
            <div className="max-w-5xl mx-auto px-6">
              <div className="text-center mb-16">
                <span className="inline-block px-3 py-1 bg-purple-500/10 border border-purple-500/20 rounded-full text-xs text-purple-300 mb-4">
                  {t("landing.pricing.badge")}
                </span>
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  {t("landing.pricing.title_pre")}{" "}
                  <span className="text-purple-400">
                    {t("landing.pricing.title_highlight")}
                  </span>
                </h2>
                <p className="text-gray-400 max-w-xl mx-auto">
                  {t("landing.pricing.subtitle")}
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
                {/* Gratuit */}
                <div className="flex flex-col p-8 rounded-2xl border border-white/10 bg-white/[0.02]">
                  <div className="flex items-center gap-2 mb-3">
                    <Star className="w-5 h-5 text-purple-400" />
                    <h3 className="text-xl font-semibold">
                      {t("landing.pricing.free_title")}
                    </h3>
                  </div>
                  <div className="mb-6">
                    <span className="text-4xl font-bold">0€</span>
                    <span className="text-gray-400 ml-2 text-sm">
                      {t("landing.pricing.free_period")}
                    </span>
                  </div>
                  <ul className="space-y-3 mb-8 flex-grow text-sm">
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300">
                        {t("landing.pricing.free_feat1")}
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300">
                        {t("landing.pricing.free_feat2")}
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300">
                        {t("landing.pricing.free_feat3")}
                      </span>
                    </li>
                  </ul>
                  <Link
                    to="/create"
                    className="w-full py-3 px-6 text-center bg-white/5 border border-white/10 text-white rounded-lg font-medium hover:bg-white/10 transition-colors"
                  >
                    {t("landing.pricing.free_cta")}
                  </Link>
                </div>

                {/* Pro */}
                <div className="flex flex-col p-8 rounded-2xl border-2 border-purple-500 bg-gradient-to-br from-purple-500/10 to-indigo-500/10 relative shadow-lg shadow-purple-500/10">
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-purple-500 text-white px-3 py-0.5 rounded-full text-xs font-semibold">
                    {t("landing.pricing.pro_badge")}
                  </div>
                  <div className="flex items-center gap-2 mb-3">
                    <Crown className="w-5 h-5 text-purple-300" />
                    <h3 className="text-xl font-semibold">
                      {t("landing.pricing.pro_title")}
                    </h3>
                  </div>
                  <div className="mb-6">
                    <span className="text-4xl font-bold">70€</span>
                    <span className="text-gray-400 ml-2 text-sm">
                      {t("landing.pricing.per_year")}
                    </span>
                  </div>
                  <ul className="space-y-3 mb-8 flex-grow text-sm">
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300">
                        {t("landing.pricing.pro_feat1")}
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300">
                        {t("landing.pricing.pro_feat2")}
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300">
                        {t("landing.pricing.pro_feat3")}
                      </span>
                    </li>
                  </ul>
                  <Link
                    to="/pricing"
                    className="w-full py-3 px-6 text-center bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-400 hover:to-indigo-400 text-white rounded-lg font-medium transition-all"
                  >
                    {t("landing.pricing.pro_cta")}
                  </Link>
                </div>
              </div>

              <p className="text-center text-sm text-gray-500 mt-8">
                <Link
                  to="/pricing"
                  className="text-purple-400 hover:text-purple-300 transition-colors"
                >
                  {t("landing.pricing.compare_link")}
                </Link>
              </p>
            </div>
          </section>

          {/* CTA Final */}
          <section className="relative py-24">
            <div className="max-w-3xl mx-auto px-6 text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                {t("landing.cta_final.title")}
              </h2>
              <p className="text-gray-400 mb-10 text-lg">
                {t("landing.cta_final.subtitle")}
              </p>
              <Link
                to="/create"
                data-testid="create-form-poll-button-final"
                className="px-10 py-4 text-base font-medium bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-400 hover:to-indigo-400 rounded-xl shadow-lg shadow-purple-500/25 text-white inline-flex"
              >
                <span className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  {t("landing.hero.cta_create")}
                </span>
              </Link>
            </div>
          </section>

          {/* Footer */}
          <footer className="border-t border-white/10 py-8">
            <div className="max-w-6xl mx-auto px-6">
              <div className="text-center space-y-4">
                <div className="flex items-center justify-center gap-2">
                  <FileText className="w-4 h-4 text-purple-400" />
                  <span className="text-sm text-gray-500">
                    {t("landing.footer.copyright", {
                      year: new Date().getFullYear(),
                    })}
                  </span>
                </div>
                <div className="flex items-center justify-center gap-2 text-sm text-gray-400">
                  <Mail className="w-4 h-4 text-purple-400" />
                  <a
                    href="mailto:support@doodates.com"
                    className="hover:text-white transition-colors"
                  >
                    support@doodates.com
                  </a>
                </div>
                <div className="flex flex-wrap justify-center items-center gap-4 text-xs">
                  <Link
                    to="/legal"
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    {t("landing.footer.legal_mentions")}
                  </Link>
                  <span className="text-gray-600">·</span>
                  <Link
                    to="/terms"
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    {t("landing.footer.terms")}
                  </Link>
                  <span className="text-gray-600">·</span>
                  <Link
                    to="/privacy"
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    {t("landing.footer.privacy")}
                  </Link>
                </div>
              </div>
            </div>
          </footer>
        </div>
      </div>
    </div>
  );
}
